import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UserCircle, Calendar as CalendarIcon, ClockCounterClockwise, PaperPlaneTilt,
  CheckCircle, XCircle, HourglassMedium, Trash, Info, ShieldCheck, FilePdf,
  Signature as SignatureIcon,
} from "@phosphor-icons/react";
import { api, apiDelete, apiError } from "@/lib/api";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignaturePicker from "@/components/SignaturePicker";
import SignaturePad from "@/components/SignaturePad";

const ATT_STATUSES = [
  { v: "present",   l: "Presente",   color: "bg-emerald-500 hover:bg-emerald-600" },
  { v: "absent",    l: "Ausente",    color: "bg-red-500 hover:bg-red-600" },
  { v: "justified", l: "Justificado", color: "bg-amber-500 hover:bg-amber-600" },
  { v: "rest",      l: "Descanso",   color: "bg-slate-500 hover:bg-slate-600" },
  { v: "vacation",  l: "Vacaciones", color: "bg-blue-500 hover:bg-blue-600" },
  { v: "permit",    l: "Permiso",    color: "bg-purple-500 hover:bg-purple-600" },
  { v: "medical",   l: "Médico",     color: "bg-orange-500 hover:bg-orange-600" },
];

const ATT_COLOR = ATT_STATUSES.reduce((acc, s) => (acc[s.v] = s.color, acc), {});
const ATT_LABEL = ATT_STATUSES.reduce((acc, s) => (acc[s.v] = s.l, acc), {});

const REQUEST_TYPES = [
  { v: "vacation",   l: "Vacaciones" },
  { v: "permit_pcg", l: "PCG · Permiso con goce" },
  { v: "permit_psg", l: "PSG · Permiso sin goce" },
  { v: "disability", l: "Incapacidad" },
  { v: "medical",    l: "Médico" },
  { v: "permit",     l: "Otro permiso" },
];

const REQ_STATUS_BADGE = {
  pending:  { l: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-300",   I: HourglassMedium },
  approved: { l: "Aprobada",  cls: "bg-emerald-100 text-emerald-800 border-emerald-300", I: CheckCircle },
  rejected: { l: "Rechazada", cls: "bg-red-100 text-red-800 border-red-300",             I: XCircle },
};

function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00");
  return d.toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" });
}

export default function MyHr() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [req, setReq] = useState({ type: "vacation", date_from: "", date_to: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const confirmDelete = useConfirmDelete();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/hr/me/summary");
      setData(r.data);
      setNote(r.data?.today?.note || "");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = useMemo(() => new Date().toLocaleDateString("es-MX", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }), []);

  const emp = data?.employee;
  const todayStatus = data?.today?.status || null;

  const markStatus = async (status) => {
    setSaving(true);
    try {
      const r = await api.post("/hr/me/attendance", { status, note });
      toast.success(`Marcado como ${ATT_LABEL[status]}`);
      setData((d) => ({
        ...d,
        today: { employee_id: r.data.employee_id, date: r.data.date, status, note,
                 marked_by: user?.name, marked_at: new Date().toISOString(), self_service: true },
      }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const submitRequest = async () => {
    if (!req.date_from || !req.date_to) { toast.error("Debes elegir rango de fechas"); return; }
    setSubmitting(true);
    try {
      await api.post("/hr/me/requests", req);
      toast.success("Solicitud enviada — pendiente de aprobación");
      setReq({ type: "vacation", date_from: "", date_to: "", note: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const cancelRequest = async (id) => {
    if (!window.confirm("¿Cancelar esta solicitud?")) return;
    try {
      await api.delete(`/hr/me/requests/${id}`);
      toast.success("Solicitud cancelada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading && !data) {
    return <div className="py-24 text-center text-slate-400 text-sm">Cargando…</div>;
  }

  if (!emp) {
    return (
      <div className="max-w-2xl mx-auto py-16 space-y-6" data-testid="myhr-unlinked">
        <div className="text-center space-y-3">
          <Info size={44} weight="duotone" className="mx-auto text-slate-400" />
          <h2 className="font-heading font-black text-xl text-slate-900">Tu usuario no está vinculado a Nómina</h2>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Contacta a un administrador para que vincule tu usuario a tu registro en Recursos Humanos.
            Después podrás marcar tu asistencia y enviar solicitudes de vacaciones / permisos aquí.
          </p>
        </div>
        {/* La firma digital NO depende de vinculación con Nómina — cualquier usuario puede capturarla */}
        <SignaturePicker />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="myhr-page">
      {/* Header — Employee card */}
      <div className="bg-gradient-to-br from-indigo-900 to-blue-900 rounded-xl p-6 text-white shadow-lg" data-testid="myhr-employee-card">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-full bg-white/15 flex items-center justify-center shrink-0">
            <UserCircle size={40} weight="duotone" className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-200">Ficha del colaborador</p>
            <h1 className="font-heading font-black text-2xl tracking-tight" data-testid="myhr-name">{emp.name}</h1>
            <p className="text-sm text-indigo-100 mt-0.5">
              {emp.position}
              {emp.area && <> · <span className="text-indigo-200">{emp.area}</span></>}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {emp.crew && <span className="text-[11px] bg-white/15 px-2 py-0.5 rounded-full">Cuadrilla {emp.crew}</span>}
              {emp.regime && <span className="text-[11px] bg-white/15 px-2 py-0.5 rounded-full">{emp.regime}</span>}
              {emp.shift_type && <span className="text-[11px] bg-white/15 px-2 py-0.5 rounded-full capitalize">{emp.shift_type}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Firma digital auditable — se estampa automáticamente en cada asistencia */}
      <SignaturePicker />

      {/* Today's attendance */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="myhr-today-section">
        <div className="flex items-center gap-2 mb-1">
          <CalendarIcon size={18} weight="duotone" className="text-blue-900" />
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Mi asistencia de hoy</p>
        </div>
        <p className="text-sm text-slate-700 mb-4 capitalize">{today}</p>

        {todayStatus && (
          <div
            className={`text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 mb-3 ${ATT_COLOR[todayStatus]?.split(" ")[0] || "bg-slate-500"}`}
            data-testid="myhr-today-status"
          >
            <CheckCircle size={16} weight="fill" />
            <span className="text-sm font-semibold">Registrado como: {ATT_LABEL[todayStatus]}</span>
          </div>
        )}
        {todayStatus && (
          <div className="text-[11px] text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3 flex items-start gap-2"
                data-testid="myhr-today-locked">
            <Info size={13} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
            <span className="flex-1">
              Tu asistencia de hoy ya fue guardada y <b>no puede cambiarse desde tu cuenta</b>.
              Si necesitas corregirla, contacta a tu <b>jefe directo, supervisor o administrador</b>
              para que la modifique desde el <b>Calendario</b>.
            </span>
            <button
              onClick={async () => {
                const ok = await confirmDelete({
                  title: "¿Borrar tu marca de asistencia?",
                  itemLabel: "Marca de hoy · self-service",
                  description: "Solo puedes borrar marcas del día actual o el anterior — queda registrado en el historial con tu motivo.",
                  minLength: 5,
                });
                if (!ok) return;
                try {
                  const d = new Date().toISOString().slice(0, 10);
                  await apiDelete(`/hr/me/attendance/${d}`, ok.reason);
                  toast.success("Marca borrada · registrada en historial");
                  window.location.reload();
                } catch (e) { toast.error(apiError(e)); }
              }}
              data-testid="myhr-today-delete"
              className="text-red-700 hover:text-red-900 underline text-[10px] font-bold whitespace-nowrap inline-flex items-center gap-1"
            >
              <Trash size={11} weight="bold" /> Borrar mi marca
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
          {ATT_STATUSES.map((s) => {
            const active = todayStatus === s.v;
            const locked = Boolean(todayStatus);   // lock once anything saved
            return (
              <button
                key={s.v}
                onClick={() => markStatus(s.v)}
                disabled={saving || locked}
                data-testid={`myhr-mark-${s.v}`}
                className={`text-white font-bold text-xs py-3 rounded-md transition ${s.color} ${
                  active ? "ring-4 ring-offset-1 ring-slate-800" : "opacity-90 hover:opacity-100"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {s.l}
              </button>
            );
          })}
        </div>

        <div>
          <Label className="text-xs text-slate-500" htmlFor="myhr-note">Nota (opcional)</Label>
          <Input
            id="myhr-note"
            data-testid="myhr-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ej: llegada tardía por lluvia, cita médica, etc."
            className="mt-1"
            maxLength={400}
          />
        </div>
      </div>

      {/* Request permit/vacation */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="myhr-request-section">
        <div className="flex items-center gap-2 mb-4">
          <PaperPlaneTilt size={18} weight="duotone" className="text-purple-700" />
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Solicitar vacaciones / permiso / médico</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Tipo</Label>
            <Select value={req.type} onValueChange={(v) => setReq((s) => ({ ...s, type: v }))}>
              <SelectTrigger className="mt-1" data-testid="myhr-req-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {REQUEST_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v} data-testid={`myhr-req-type-${t.v}`}>{t.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Desde</Label>
            <Input type="date" value={req.date_from} data-testid="myhr-req-from"
                   onChange={(e) => setReq((s) => ({ ...s, date_from: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Hasta</Label>
            <Input type="date" value={req.date_to} data-testid="myhr-req-to"
                   onChange={(e) => setReq((s) => ({ ...s, date_to: e.target.value }))} className="mt-1" />
          </div>
          <div className="flex items-end">
            <Button
              onClick={submitRequest}
              disabled={submitting}
              data-testid="myhr-req-submit"
              className="w-full bg-blue-900 hover:bg-blue-800 gap-2"
            >
              <PaperPlaneTilt size={14} weight="bold" /> {submitting ? "Enviando…" : "Enviar"}
            </Button>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs text-slate-500">Motivo (opcional)</Label>
            <Textarea
              rows={2}
              value={req.note}
              data-testid="myhr-req-note"
              onChange={(e) => setReq((s) => ({ ...s, note: e.target.value }))}
              className="mt-1 text-sm"
              placeholder="Ej: viaje familiar, control médico anual, etc."
              maxLength={400}
            />
          </div>
        </div>
      </div>

      {/* Own requests list */}
      {data.requests?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="myhr-requests-list">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={18} weight="duotone" className="text-slate-500" />
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Mis solicitudes</p>
          </div>
          <div className="space-y-2">
            {data.requests.map((r) => {
              const badge = REQ_STATUS_BADGE[r.status] || REQ_STATUS_BADGE.pending;
              const B = badge.I;
              const typeLabel = REQUEST_TYPES.find((t) => t.v === r.type)?.l || r.type;
              return (
                <div key={r.id} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0"
                     data-testid={`myhr-request-${r.id}`}>
                  <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${badge.cls} flex items-center gap-1`}>
                    <B size={11} weight="bold" /> {badge.l}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{typeLabel} · {fmtDay(r.date_from)} → {fmtDay(r.date_to)}</p>
                    {r.note && <p className="text-xs text-slate-500 truncate">{r.note}</p>}
                    {r.review_note && (
                      <p className="text-xs mt-0.5 text-slate-600 italic">
                        Nota del revisor: {r.review_note}
                      </p>
                    )}
                  </div>
                  {r.status === "pending" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => cancelRequest(r.id)}
                      data-testid={`myhr-request-${r.id}-cancel`}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Attendance history */}
      {data.history?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="myhr-history">
          <div className="flex items-center gap-2 mb-3">
            <CalendarIcon size={18} weight="duotone" className="text-slate-500" />
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Últimos 14 días</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {data.history.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-3 bg-slate-50 rounded-md">
                <span className={`h-2 w-2 rounded-full ${ATT_COLOR[h.status]?.split(" ")[0] || "bg-slate-300"}`} />
                <span className="font-mono text-slate-700">{fmtDay(h.date)}</span>
                <span className="text-slate-500 flex-1 truncate">{ATT_LABEL[h.status] || h.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumen personal del periodo (elegido por el operador) */}
      <MyPeriodSummary />
      <MyEppSection />
    </div>
  );
}

/**
 * MyPeriodSummary — Resumen personal del colaborador para el rango elegido.
 * Muestra conteos por estatus, % de asistencia efectiva y solicitudes en el
 * periodo. Query: GET /hr/me/period-summary?date_from=&date_to=.
 */
function MyPeriodSummary() {
  const today = new Date().toISOString().slice(0, 10);
  const first = new Date();
  first.setDate(1);
  const [range, setRange] = useState({
    date_from: first.toISOString().slice(0, 10),
    date_to: today,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!range.date_from || !range.date_to) return;
    setLoading(true);
    api.get(`/hr/me/period-summary?date_from=${range.date_from}&date_to=${range.date_to}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const statusInfo = {
    present:   { l: "Presente", color: "bg-emerald-500" },
    absent:    { l: "Ausente", color: "bg-red-500" },
    justified: { l: "Justificado", color: "bg-amber-500" },
    rest:      { l: "Descanso", color: "bg-slate-500" },
    vacation:  { l: "Vacaciones", color: "bg-blue-500" },
    permit:    { l: "Permiso", color: "bg-purple-500" },
    medical:   { l: "Médico", color: "bg-orange-500" },
    unmarked:  { l: "Sin marcar", color: "bg-slate-300" },
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="myhr-period-summary">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ClockCounterClockwise size={18} weight="duotone" className="text-indigo-700" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          Resumen del periodo que elijas consultar
        </p>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-[10px] uppercase text-slate-500 font-bold">Del</Label>
          <Input type="date" value={range.date_from}
                 onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
                 className="h-8 w-36" data-testid="myhr-period-from" />
          <Label className="text-[10px] uppercase text-slate-500 font-bold">Al</Label>
          <Input type="date" value={range.date_to}
                 onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))}
                 className="h-8 w-36" data-testid="myhr-period-to" />
        </div>
      </div>

      {loading && <p className="text-xs text-slate-400 italic">Cargando resumen…</p>}
      {data && !loading && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Asistencia efectiva</p>
              <p className="font-heading font-black text-3xl text-emerald-800" data-testid="myhr-period-pct">
                {data.attendance_pct}%
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {data.counts.present + data.counts.justified} / {data.workable_days} laborables
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Días en el rango</p>
              <p className="font-heading font-black text-3xl text-slate-800">{data.total_days}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">del {data.date_from} al {data.date_to}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest font-bold text-blue-700">Vacaciones + permisos</p>
              <p className="font-heading font-black text-3xl text-blue-800">
                {data.counts.vacation + data.counts.permit + data.counts.medical}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">días combinados</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Solicitudes</p>
              <p className="font-heading font-black text-3xl text-amber-800">{data.requests.length}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">emitidas en el rango</p>
            </div>
          </div>

          {/* Breakdown por estatus */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {Object.entries(data.counts).filter(([, v]) => v > 0).map(([k, v]) => {
              const info = statusInfo[k] || { l: k, color: "bg-slate-300" };
              return (
                <div key={k} className="flex items-center gap-2 text-xs py-1.5 px-3 bg-slate-50 rounded-md"
                     data-testid={`myhr-period-count-${k}`}>
                  <span className={`h-2 w-2 rounded-full ${info.color}`} />
                  <span className="font-mono text-slate-700">{info.l}</span>
                  <span className="ml-auto font-bold text-slate-900">{v}</span>
                </div>
              );
            })}
          </div>

          {data.attendance_pct >= 95 ? (
            <p className="text-xs text-emerald-700 italic border-t border-emerald-100 pt-2">
              ✓ Excelente asistencia — mantén el ritmo.
            </p>
          ) : data.attendance_pct >= 85 ? (
            <p className="text-xs text-amber-700 italic border-t border-amber-100 pt-2">
              ⚠ Asistencia por debajo del objetivo (95%). Revisa los días marcados como ausente/justificado.
            </p>
          ) : (
            <p className="text-xs text-red-700 italic border-t border-red-100 pt-2">
              ⚠ Asistencia crítica ({data.attendance_pct}%). Contacta a tu jefe directo para regularizar tu situación.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* =============== Mi EPP · Portal Colaborador =============== */
export function MyEppSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null); // delivery object being signed
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/epp/me/deliveries");
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (id, folio) => {
    try {
      const r = await api.get(`/epp/me/deliveries/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `epp-${folio || id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitSign = async () => {
    if (!sig) { toast.warning("Dibuja tu firma"); return; }
    setBusy(true);
    try {
      await api.post(`/epp/me/deliveries/${signing.id}/sign`, { signature_data_url: sig });
      toast.success(`Constancia ${signing.folio || ""} firmada`);
      setSigning(null); setSig("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-slate-400 italic text-sm">Cargando mi EPP…</div>;
  if (!data || !data.employee_id) return null;
  const t = data.totals || {};
  const rows = data.deliveries || [];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="myhr-epp-section">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={18} weight="duotone" className="text-blue-800" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          Mi EPP · Equipo de protección personal recibido
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
          <p className="text-[10px] uppercase font-bold text-blue-700">Entregas</p>
          <p className="font-heading font-black text-3xl text-blue-800" data-testid="myhr-epp-deliveries">{t.deliveries || 0}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-center">
          <p className="text-[10px] uppercase font-bold text-slate-600">Unidades</p>
          <p className="font-heading font-black text-3xl text-slate-800">{t.items || 0}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
          <p className="text-[10px] uppercase font-bold text-emerald-700">Costo total</p>
          <p className="font-heading font-black text-2xl text-emerald-800">
            ${Number(t.cost || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-6">Aún no tienes EPP registrado.</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="myhr-epp-table">
            <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Folio</th>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">EPP</th>
                <th className="text-center px-3 py-2">Uds.</th>
                <th className="text-center px-3 py-2">Firmado</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((d) => {
                const items = d.items || [];
                const total = items.reduce((s, i) => s + Number(i.qty || 0), 0);
                const signed = !!d.employee_signature;
                return (
                  <tr key={d.id} data-testid={`myhr-epp-row-${d.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{d.folio}</td>
                    <td className="px-3 py-2 text-slate-700">{d.date}</td>
                    <td className="px-3 py-2">
                      {items.slice(0, 3).map((it, i) => (
                        <span key={i} className="inline-block text-[10px] mr-1 mb-0.5 px-1.5 py-0.5 bg-blue-50 border border-blue-200 rounded">
                          {it.item_description || it.item_code}
                        </span>
                      ))}
                      {items.length > 3 && (
                        <span className="text-[10px] text-slate-500">+{items.length - 3} más</span>
                      )}
                    </td>
                    <td className="text-center px-3 py-2 font-bold">{total}</td>
                    <td className="text-center px-3 py-2">
                      {signed
                        ? <CheckCircle size={16} weight="fill" className="inline text-emerald-600" />
                        : <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5">Pendiente</span>}
                    </td>
                    <td className="text-right px-3 py-2">
                      <div className="inline-flex items-center gap-1">
                        {!signed && (
                          <button onClick={() => { setSigning(d); setSig(""); }}
                                  data-testid={`myhr-epp-sign-${d.id}`}
                                  className="text-amber-700 hover:text-amber-900 gap-1 inline-flex items-center text-xs font-bold">
                            <SignatureIcon size={13} weight="bold" /> Firmar
                          </button>
                        )}
                        <button onClick={() => downloadPdf(d.id, d.folio)}
                                data-testid={`myhr-epp-pdf-${d.id}`}
                                className="text-blue-700 hover:text-blue-900 gap-1 inline-flex items-center text-xs font-bold ml-2">
                          <FilePdf size={13} weight="bold" /> PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog para firmar constancia pendiente — auto-servicio EPP */}
      <Dialog open={!!signing} onOpenChange={(o) => { if (!o) { setSigning(null); setSig(""); } }}>
        <DialogContent className="bg-white max-w-md" data-testid="myhr-epp-sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <SignatureIcon size={18} weight="fill" className="text-blue-900" />
              Firmar constancia {signing?.folio}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-600 space-y-2 mb-3">
            <p><b>Fecha:</b> {signing?.date}</p>
            <p className="text-[11px] leading-relaxed border border-blue-200 bg-blue-50/40 rounded p-2">
              <b>Declaro:</b> haber recibido en buen estado el EPP arriba descrito,
              comprometiéndome a portarlo, mantenerlo y reportar cualquier daño.
            </p>
          </div>
          <div className="bg-white border-2 border-slate-200 rounded p-2">
            <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">Firma aquí</p>
            <SignaturePad value={sig} onChange={setSig} width={340} height={110} testid="myhr-epp-sig-pad" />
            {sig && (
              <button onClick={() => setSig("")} className="text-[10px] text-slate-500 underline mt-1"
                      data-testid="myhr-epp-sig-clear">Limpiar</button>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => { setSigning(null); setSig(""); }}>Cancelar</Button>
            <Button onClick={submitSign} disabled={busy || !sig}
                    data-testid="myhr-epp-sign-confirm"
                    className="bg-emerald-700 hover:bg-emerald-800 gap-1.5">
              <CheckCircle size={14} weight="bold" /> {busy ? "Firmando…" : "Confirmar y firmar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
